// Derives the GRAVITY level and display colour from the numeric score
// (0-100). `risk_score` mesure la GRAVITÉ du dossier : élevé = dossier
// difficile. Retourne { level, color, pillBg, scoreColor, barColor }.
//
// Barème unifié (page dossier + bureau + sidebar — le même chiffre ne
// peut plus être vert d'un côté et rouge de l'autre) :
//   ≤30  Faible   vert   #16a34a
//   31-60 Modérée ambre  #f59e0b
//   ≥61  Élevée   orange #ea580c
//
// JAMAIS de rouge pur sur un score : le rouge reste réservé aux
// échéances. L'ancienne bande `critical` (≥80, rouge #b91c1c) est donc
// supprimée — elle est absorbée par `high`.
export function getRiskBand(score) {
  const s = Number(score) || 0;
  if (s >= 61) return { level: 'high', color: '#c2410c', pillBg: '#ea580c', scoreColor: '#ea580c', barColor: '#ea580c' };
  if (s >= 31) return { level: 'moderate', color: '#b45309', pillBg: '#f59e0b', scoreColor: '#f59e0b', barColor: '#f59e0b' };
  return { level: 'low', color: '#15803d', pillBg: '#16a34a', scoreColor: '#16a34a', barColor: '#16a34a' };
}

/**
 * Le barème des QUATRE DIMENSIONS — le même que celui du score.
 *
 * TOUT CE QUI MONTE EST MAUVAIS, SANS EXCEPTION
 * ---------------------------------------------
 * Les quatre dimensions (`risk_urgency`, `risk_financial`,
 * `risk_complexity`, `risk_legal_strength`) mesurent un RISQUE : élevé =
 * mauvais, comme `risk_score` qui les agrège. Aucune n'est inversée, et
 * c'est une décision, pas un constat — `risk_legal_strength` portait le
 * libellé « SOLIDITÉ », où élevé aurait voulu dire bon, au milieu de trois
 * dimensions qui disaient l'inverse. Une seule échelle retournée est un
 * piège permanent : c'est elle qui a produit deux écritures backend
 * opposées pour le même champ (le scan léger posait `risk_total`, Contract
 * Guard posait `100 - risk_total`). Le libellé a donc été changé, pas la
 * couleur.
 *
 * POURQUOI CE SEUIL, ET PAS UN AUTRE
 * ----------------------------------
 * 61/31, celui de `getRiskBand` ci-dessus — le seul barème que le produit
 * déclare unifié, et celui de l'email `analysis_ready`. Il y en avait six
 * en circulation : 61/31 ici, 70/50 pour les sous-scores, 70/40 dans la
 * cinématique, 70/40 sur la page dossier, 61/31 dans le bureau v3, 61/31
 * sur le dossier partagé. Avec 70/40, une dimension à 65 s'affichait ambre
 * pendant qu'un score de 65 s'affichait orange — le même nombre, deux
 * lectures, sur le même écran.
 *
 * La palette est celle de `getRiskBand`, donc JAMAIS de rouge pur : le
 * rouge reste réservé aux échéances. Une dimension élevée est orange.
 */
export function getDimensionTone(value) {
  const band = getRiskBand(value);
  return {
    level: band.level,
    color: band.scoreColor,
    soft: band.level === 'high' ? '#fff7ed' : band.level === 'moderate' ? '#fffbeb' : '#f0fdf4',
    border: band.level === 'high' ? '#fed7aa' : band.level === 'moderate' ? '#fde68a' : '#bbf7d0',
  };
}

// Ancien barème des sous-scores (70/50, rouge pur). Conservé comme façade
// le temps que ses appelants migrent : il DÉLÈGUE, il ne décide plus.
export function getSubScoreColor(value) {
  const tone = getDimensionTone(value);
  return { text: tone.color, bar: tone.color };
}

// Maps (level, language) → localized risk label key. Keep i18n lookup in the caller.
export function getRiskLabelKey(level) {
  const validLevels = ['low', 'moderate', 'high', 'critical'];
  return validLevels.includes(level) ? level : 'moderate';
}
